import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  MessageCircle,
  Users,
  Eye,
  Flag,
  CheckCircle,
  XCircle,
  EyeOff,
  VolumeX,
  AlertTriangle
} from 'lucide-react';
import type { ModerationItem } from '@/pages/admin/moderation';

interface ModerationItemCardProps {
  item: ModerationItem;
  onApprove: () => void;
  onHide: () => void;
  onDelete: () => void;
  onMute?: () => void;
  showMuteButton?: boolean;
  isProcessing?: boolean;
  processingAction?: string;
}

export function ModerationItemCard({
  item,
  onApprove,
  onHide,
  onDelete,
  onMute,
  showMuteButton = false,
  isProcessing = false,
  processingAction
}: ModerationItemCardProps) {
  const [expanded, setExpanded] = useState(false);

  const getPriorityBadge = (priority: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
      'low': 'secondary',
      'medium': 'default',
      'high': 'destructive'
    };
    return <Badge variant={variants[priority] || 'secondary'} className="capitalize">{priority}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      'pending': 'secondary',
      'approved': 'default',
      'rejected': 'destructive',
      'flagged': 'destructive',
      'hidden': 'outline'
    };
    const labels: Record<string, string> = {
      'pending': 'Pending',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'flagged': 'Flagged',
      'hidden': 'Hidden'
    };
    return <Badge variant={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'comment': return <MessageCircle className="h-4 w-4" />;
      case 'user': return <Users className="h-4 w-4" />;
      case 'stream': return <Eye className="h-4 w-4" />;
      default: return <Flag className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: string): string => {
    switch (type) {
      case 'comment': return 'text-blue-600 bg-blue-100';
      case 'user': return 'text-purple-600 bg-purple-100';
      case 'stream': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow">
      <CardContent className="p-4 space-y-3">
        {/* Header with badges */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${getTypeColor(item.type)}`}>
            {getTypeIcon(item.type)}
            <span className="font-medium capitalize">{item.type}</span>
          </div>
          {getPriorityBadge(item.priority)}
          {getStatusBadge(item.status)}
          {item.reportCount && item.reportCount > 1 && (
            <Badge variant="outline" className="text-xs">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {item.reportCount} reports
            </Badge>
          )}
        </div>

        {/* Author Info */}
        <div className="flex items-center gap-2">
          <Avatar className="h-6 w-6">
            <AvatarImage src={item.author.avatar} />
            <AvatarFallback className="text-xs">
              {item.author.name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{item.author.name}</p>
            <p className="text-xs text-muted-foreground truncate">
              {formatDate(item.createdAt)}
              {item.stream && ` • ${item.stream.title}`}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="text-sm line-clamp-3">{item.content}</p>
        </div>

        {/* Reason if flagged */}
        {item.reason && (
          <div className="text-xs text-muted-foreground bg-yellow-50 border border-yellow-200 rounded p-2">
            <span className="font-medium">Flag reason:</span> {item.reason}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            onClick={onApprove}
            disabled={isProcessing || processingAction === 'approve'}
            className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
          >
            {isProcessing && processingAction === 'approve' ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-green-600 border-t-transparent" />
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-1" />
                Approve
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onHide}
            disabled={isProcessing || processingAction === 'hide'}
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
          >
            {isProcessing && processingAction === 'hide' ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
            ) : (
              <>
                <EyeOff className="h-4 w-4 mr-1" />
                Hide
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={onDelete}
            disabled={isProcessing || processingAction === 'delete'}
          >
            {isProcessing && processingAction === 'delete' ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-destructive border-t-transparent" />
            ) : (
              <>
                <XCircle className="h-4 w-4 mr-1" />
                Delete
              </>
            )}
          </Button>
          {showMuteButton && item.type === 'user' && onMute && (
            <Button
              size="sm"
              variant="secondary"
              onClick={onMute}
              disabled={isProcessing || processingAction === 'mute'}
            >
              {isProcessing && processingAction === 'mute' ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-secondary border-t-transparent" />
              ) : (
                <>
                  <VolumeX className="h-4 w-4 mr-1" />
                  Mute
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
